import memoizeOne from 'memoize-one';
import React, { Component, Fragment, ReactNode } from 'react';
import { ReferenceBasketVersion } from '~/Api/PriceReferencing';
import { validateHistoricProductDataViewDate } from '~/Api/Product';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import {
	BgMaxPrice as ApiBgMaxPrice,
	ErrorCode,
	Product,
	ProductUpdate,
	ValidationResult,
} from '~/Api/User/ProductGroup/BgMaxPrice';
import Calculation from '~/BgMaxPrice/Calculation/Calculation';
import ParametersPanel, { Values as ReferenceValues } from '~/BgMaxPrice/ParametersPanel';
import ProductsTable, { ProductRow, ProductValues } from '~/BgMaxPrice/ProductsTable/ProductsTable';
import CopyGroupProductActionPanel from '~/CopyGroupProduct/CopyGroupProductActionPanel';
import { formatNumericHumanDate } from '~/DateTime';
import { parsePackageSize, PricedProduct, Product as GeneralProduct } from '~/Product';
import ProductDetailModal, { PricesContainer } from '~/ProductDetailModal';
import ProductGroupRemoveProductModal from '~/ProductGroupRemoveProductModal';
import ProductInstructions from '~/ProductInstructions';
import ActivityIndicator from '~/UI/ActivityIndicator';
import Heading from '~/UI/Heading/Heading';

import { ProductProvider } from './ProductProvider';
import { ReferencePriceValue } from './ProductsTable/Row/Row';
import styles from './styles.less';

interface Props {
	bulgarianProducts: ProductRow[];
	currency: string | null;
	detailProductId: string | null;
	detailProductTabName: string;
	group: UserProductGroup;
	header: ReactNode;
	bgMaxPrice: ApiBgMaxPrice | null;
	isFetching: boolean;
	otherProducts: ProductRow[];
	pricedProducts: Array<PricedProduct<Product>>;
	referenceBasketVersion: ReferenceBasketVersion | null;
	referencedProducts: ProductRow[];
	validationResult: ValidationResult | null;
	onCloseDetail(): void;
	onCurrencyChange(currency: string): void;
	onFetch(group: UserProductGroup): Promise<void>;
	onPrune(group: UserProductGroup): void;
	onSave(group: UserProductGroup): Promise<void>;
	onShowDetail(productId: string | null, tabName: string): void;
	onUpdate(
		group: UserProductGroup,
		basketVersion: string | null,
		packageSize: number | null,
		historicProductDataViewDate: Date | null,
		products: ProductUpdate[]
	): void;
	productDetailLink(product: { id: string }): { pathname: string; search: string };
	onDeleteItems(group: UserProductGroup, products: GeneralProduct[], onSuccess: () => void): Promise<void>;
}

interface Values {
	product: { [productId: string]: ProductValues };
	reference: ReferenceValues;
}

interface State {
	lastSeenProductIdDetail: string | null;
	lastSelectedProductId: string | null;
	removeProduct: GeneralProduct | null;
	values: Values | null;
	checkedRows: GeneralProduct[];
}

export default class BgMaxPrice extends Component<Props, State> {
	public static defaultProps = {
		detailProductId: null,
		detailProductTabName: 'info',
		bgMaxPrice: null,
		validationResult: null,
	};

	public state: State = {
		lastSeenProductIdDetail: null,
		lastSelectedProductId: null,
		removeProduct: null,
		values: null,
		checkedRows: [],
	};

	private readonly createValues = memoizeOne((bgMaxPrice: ApiBgMaxPrice | null, values: Values | null): Values => {
		const historicProductDataViewDate = bgMaxPrice?.reference.historicProductDataViewDate ?? null;

		return {
			product:
				bgMaxPrice?.products.reduce((products: { [productId: string]: ProductValues }, product) => {
					const currentValues = values?.product[product.product.id];
					const hasSelectedValidReferencePrice =
						currentValues !== undefined &&
						product.referencePrices.find(
							(referencePrice) => referencePrice.calculatedPrice.type === currentValues.referencePrice.value?.value
						) !== undefined;
					const referencePrice = product.referencePrices.find((price) => price.calculatedPrice.selected);

					products[product.product.id] = {
						packageSize: currentValues?.packageSize ?? {
							isValid: true,
							value: product.packageSizeForCalculation !== null ? String(product.packageSizeForCalculation) : '',
						},
						packageSizeForSingleUnit: currentValues?.packageSizeForSingleUnit ?? {
							isValid: true,
							value: product.packageSizeForSingleUnit !== null ? String(product.packageSizeForSingleUnit) : '',
						},
						referencePrice: hasSelectedValidReferencePrice
							? currentValues.referencePrice
							: {
									isValid:
										product.referencePrices.length === 0 ||
										referencePrice !== undefined ||
										product.allowsEmptyCalculator.exFactory,
									value:
										referencePrice !== undefined
											? {
													value: referencePrice.calculatedPrice.type,
													parentValue: null,
													prompt: null,
											  }
											: null,
							  },
						selected: currentValues?.selected ?? product.selected,
					};
					return products;
				}, {}) ?? {},
			reference: values?.reference ?? {
				basketVersion: bgMaxPrice?.reference.referenceBasketVersion ?? '',
				packageSize: {
					value: String(bgMaxPrice?.reference.packageSize ?? ''),
					isValid: true,
				},
				historicProductDataViewDate: {
					value: historicProductDataViewDate !== null ? formatNumericHumanDate(historicProductDataViewDate) : '',
					dateValue: historicProductDataViewDate,
					isValid: true,
				},
			},
		};
	});

	public componentDidMount() {
		const { group, onFetch } = this.props;
		onFetch(group);
	}

	public componentDidUpdate(prevProps: Props) {
		const { group, validationResult, onFetch } = this.props;

		if (prevProps.group !== group) {
			onFetch(group);
		} else if (prevProps.validationResult !== validationResult) {
			this.processValidationResult(validationResult);
		}
	}

	public componentWillUnmount() {
		const { group, onPrune } = this.props;
		onPrune(group);
	}

	private readonly checkRow = (product: GeneralProduct, checked: boolean) => {
		this.setState((prevState) => ({
			checkedRows: checked
				? [...prevState.checkedRows, product]
				: prevState.checkedRows.filter((row) => row.id !== product.id),
		}));
	};

	private readonly countRows = () => {
		return this.state.checkedRows.length;
	};

	onDeleteItems = async (group: UserProductGroup, products: GeneralProduct[]) => {
		await this.props.onDeleteItems(group, products, () => {
			this.setState({ checkedRows: [] });
		});
	};

	private readonly onClickCancelDelete = () => {
		this.setState({ checkedRows: [] });
	};

	private handleProductsCopied = () => {
		this.setState((prevState) => ({
			checkedRows: [],
		}));
	};

	public render() {
		const {
			props: {
				header,
				bgMaxPrice,
				referenceBasketVersion,
				group,
				pricedProducts,
				bulgarianProducts,
				referencedProducts,
				otherProducts,
				isFetching,
				detailProductId,
				productDetailLink,
				detailProductTabName,
				currency,
				onShowDetail,
				onSave,
				onCurrencyChange,
			},
			state: { lastSelectedProductId, lastSeenProductIdDetail, removeProduct, checkedRows },
		} = this;

		const values = this.resolveValues();
		const referencePackageSize = parsePackageSize(values.reference.packageSize.value).value;

		if (bgMaxPrice === null && isFetching) {
			return <ActivityIndicator />;
		}

		return (
			<Fragment>
				{header}

				<section className={styles.section}>
					{detailProductId !== null ? (
						<PricesContainer currency={currency} productId={detailProductId}>
							<ProductProvider bgMaxPrice={bgMaxPrice} id={detailProductId}>
								{(product) => (
									<ProductDetailModal
										currency={currency}
										groupId={group.id}
										isFetching={false}
										product={product}
										productId={detailProductId}
										products={pricedProducts}
										selectedTabName={detailProductTabName}
										shareLink={`${window.location.origin}/search/${encodeURIComponent(detailProductId)}/${
											detailProductTabName === 'info' ? '' : encodeURIComponent(detailProductTabName)
										}`}
										onClose={this.handleCloseDetail}
										onCurrencyChange={onCurrencyChange}
										onChange={(productId) => {
											onShowDetail(productId, detailProductTabName);
										}}
										onSelectTab={(tabName) => {
											onShowDetail(detailProductId, tabName);
										}}
									/>
								)}
							</ProductProvider>
						</PricesContainer>
					) : null}

					{bgMaxPrice !== null && referenceBasketVersion !== null ? <Calculation bgMaxPrice={bgMaxPrice} /> : null}

					<ParametersPanel
						group={group}
						values={values.reference}
						onChange={this.referenceValuesChanged}
						onSave={() => {
							onSave(group);
						}}
					/>

					{pricedProducts.length === 0 ? <ProductInstructions /> : null}

					{pricedProducts.length > 0 ? (
						<div className={styles.tableHeader}>
							<div className={styles.in}>
								<div className={''}>
									<Heading level={2}>Bulgarian products</Heading>
								</div>
							</div>
						</div>
					) : null}

					{pricedProducts.length > 0 ? (
						<ProductsTable
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							historicData={Boolean(bgMaxPrice?.reference.historicProductDataViewDate)}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							lastSelectedProductId={lastSelectedProductId}
							products={bulgarianProducts}
							productValues={values.product}
							referencePackageSize={referencePackageSize}
							onChange={this.handleProductValuesChange}
							onSave={() => {
								onSave(group);
							}}
							onSelect={this.productSelected}
						/>
					) : null}

					{pricedProducts.length > 0 ? (
						<div className={styles.tableHeader}>
							<div className={styles.in}>
								<Heading level={2}>Reference basket products</Heading>
							</div>
						</div>
					) : null}

					{pricedProducts.length > 0 ? (
						<ProductsTable
							showReferenceDataSource
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							historicData={Boolean(bgMaxPrice?.reference.historicProductDataViewDate)}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							lastSelectedProductId={lastSelectedProductId}
							products={referencedProducts}
							productValues={values.product}
							referencePackageSize={referencePackageSize}
							onChange={this.handleProductValuesChange}
							onSave={() => {
								onSave(group);
							}}
							onSelect={this.productSelected}
						/>
					) : null}

					{pricedProducts.length > 0 && otherProducts.length > 0 ? (
						<div className={styles.tableHeader}>
							<div className={styles.in}>
								<div className={''}>
									<Heading level={2}>Other</Heading>
								</div>
							</div>
						</div>
					) : null}

					{pricedProducts.length > 0 && otherProducts.length > 0 ? (
						<ProductsTable
							disableSelection
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							historicData={Boolean(bgMaxPrice?.reference.historicProductDataViewDate)}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							lastSelectedProductId={lastSelectedProductId}
							products={otherProducts}
							productValues={values.product}
							referencePackageSize={referencePackageSize}
							onChange={this.handleProductValuesChange}
							onSave={() => {
								onSave(group);
							}}
							onSelect={this.productSelected}
						/>
					) : null}

					{checkedRows.length > 0 ? (
						<CopyGroupProductActionPanel
							group={group}
							products={checkedRows}
							selectedProductsCount={this.countRows()}
							onClickCancelDelete={this.onClickCancelDelete}
							onClickRemoveRow={this.onDeleteItems}
							onProductsCopied={this.handleProductsCopied}
						/>
					) : null}

					{removeProduct !== null ? (
						<ProductGroupRemoveProductModal
							group={group}
							product={removeProduct}
							onClose={() => {
								this.setState({ removeProduct: null });
							}}
							onConfirm={async () => {
								await onSave(group);
							}}
							onSuccess={() => {
								this.setState({ removeProduct: null });
							}}
						/>
					) : null}
				</section>
			</Fragment>
		);
	}

	private readonly handleCloseDetail = () => {
		const { detailProductId, onCloseDetail } = this.props;

		this.setState({ lastSeenProductIdDetail: detailProductId });
		onCloseDetail();
	};

	private readonly handleProductValuesChange = (
		item: Product,
		referencePrice: ReferencePriceValue | null,
		packageSize: string,
		packageSizeForSingleUnit: string
	) => {
		const values = this.resolveValues();
		const currentValue = values.product[item.product.id];
		const invokeSave = currentValue.referencePrice.value?.value !== referencePrice?.value;

		this.setState(
			{
				values: {
					product: {
						...values.product,
						[item.product.id]: {
							packageSize: { value: packageSize, isValid: null },
							packageSizeForSingleUnit: {
								value: packageSizeForSingleUnit,
								isValid: null,
							},
							referencePrice: {
								value: referencePrice,
								isValid:
									item.referencePrices.length === 0 || referencePrice !== null || item.allowsEmptyCalculator.exFactory,
							},
							selected: currentValue.selected,
						},
					},
					reference: values.reference,
				},
			},
			() => {
				this.update();
				if (invokeSave) {
					this.props.onSave(this.props.group);
				}
			}
		);
	};

	private readonly referenceValuesChanged = (reference: ReferenceValues) => {
		const values = this.resolveValues();
		const invokeSave = reference.basketVersion !== values.reference.basketVersion;

		this.setState({ values: { ...values, reference } }, () => {
			this.update();
			if (invokeSave) {
				this.props.onSave(this.props.group);
			}
		});
	};

	private processValidationResult(validationResult: ValidationResult | null) {
		if (validationResult === null) {
			return;
		}

		const values = this.resolveValues();
		const referenceValues = {
			...values.reference,
			packageSize: {
				...values.reference.packageSize,
				isValid: !validationResult.referencePackageSizeIsMissing,
			},
		};
		const productValues = { ...values.product };

		validationResult.productErrors.forEach((error) => {
			if (error.productId === null || !(error.productId in productValues)) {
				return;
			}
			const values = productValues[error.productId];
			if (error.code === ErrorCode.requiredProductSettingsMissing) {
				productValues[error.productId] = {
					...values,
					packageSize: { ...values.packageSize, isValid: false },
					packageSizeForSingleUnit: {
						...values.packageSizeForSingleUnit,
						isValid: false,
					},
				};
			} else if (error.code === ErrorCode.illegalPriceTypeSelected) {
				productValues[error.productId] = {
					...values,
					referencePrice: { ...values.referencePrice, isValid: false },
				};
			}
		});

		this.setState({
			values: { reference: referenceValues, product: productValues },
		});
	}

	private readonly productSelected = (item: Product, selected: boolean, range: boolean) => {
		const values = this.resolveValues();
		const currentValue = values.product[item.product.id];
		const productValues = { ...values.product };

		if (range) {
			this.selectProductRange(productValues, item.product.id, this.state.lastSelectedProductId, selected);
		} else {
			productValues[item.product.id] = { ...currentValue, selected };
		}

		this.setState(
			{
				lastSelectedProductId: item.product.id,
				values: { reference: values.reference, product: productValues },
			},
			this.update
		);
	};

	private resolveValues(): Values {
		const {
			props: { bgMaxPrice },
			state: { values },
		} = this;
		return this.createValues(bgMaxPrice, values);
	}

	private selectProductRange(
		productValues: { [productId: string]: ProductValues },
		selectedProductId: string,
		lastSelectedProductId: string | null,
		selected: boolean
	) {
		const { pricedProducts } = this.props;
		const selectedIndex = pricedProducts.findIndex((pricedProduct) => pricedProduct.product.id === selectedProductId);
		const lastSelectedIndex = pricedProducts.findIndex(
			(pricedProduct) => pricedProduct.product.id === lastSelectedProductId
		);

		if (selectedIndex >= 0 && lastSelectedIndex >= 0) {
			const startIndex = Math.min(selectedIndex, lastSelectedIndex);
			const endIndex = Math.max(selectedIndex, lastSelectedIndex);
			for (let i = startIndex; i <= endIndex; i += 1) {
				const values = productValues[pricedProducts[i].product.id];
				productValues[pricedProducts[i].product.id] = { ...values, selected };
			}
		}
	}

	private readonly update = () => {
		const values = this.resolveValues();
		const referenceValues = { ...values.reference };
		const productValues = { ...values.product };

		const referencePackageSize = parsePackageSize(referenceValues.packageSize.value);
		referenceValues.packageSize.isValid = referencePackageSize.isValid;

		referenceValues.historicProductDataViewDate.isValid =
			referenceValues.historicProductDataViewDate.value === '' ||
			(referenceValues.historicProductDataViewDate.dateValue !== null &&
				validateHistoricProductDataViewDate(referenceValues.historicProductDataViewDate.dateValue));

		let hasValidationError =
			referencePackageSize.isValid === false || referenceValues.historicProductDataViewDate.isValid === false;

		const products: ProductUpdate[] = [];

		Object.keys(productValues).forEach((productId) => {
			const values = productValues[productId];
			const { referencePrice, packageSize, packageSizeForSingleUnit } = values;

			const parsedPackageSize = parsePackageSize(packageSize.value);
			const parsedPackageSizeForSingleUnit = parsePackageSize(packageSizeForSingleUnit.value);

			hasValidationError =
				hasValidationError ||
				!referencePrice.isValid ||
				parsedPackageSize.isValid === false ||
				parsedPackageSizeForSingleUnit.isValid === false;

			productValues[productId] = {
				...values,
				packageSize: {
					...values.packageSize,
					isValid: parsedPackageSize.isValid,
				},
				packageSizeForSingleUnit: {
					...values.packageSizeForSingleUnit,
					isValid: parsedPackageSizeForSingleUnit.isValid,
				},
			};

			products.push(
				new ProductUpdate(
					productId,
					referencePrice.value?.value ?? null,
					parsedPackageSize.value,
					parsedPackageSizeForSingleUnit.value,
					values.selected
				)
			);
		});

		this.setState({
			values: { reference: referenceValues, product: productValues },
		});

		if (!hasValidationError) {
			this.props.onUpdate(
				this.props.group,
				referenceValues.basketVersion,
				referencePackageSize.value,
				referenceValues.historicProductDataViewDate.dateValue,
				products
			);
		}
	};
}
